import pako from "pako";
 
export function base64ToBytes(base64) {
    if (base64 == null) {
      throw new Error("base64ToBytes: missing input");
    }
  
    // Query strings often turn '+' into space; and many producers emit base64url.
    let normalized = String(base64).trim().replace(/ /g, "+").replace(/-/g, "+").replace(/_/g, "/");
  
    // Strip characters that can show up from copying/wrapping.
    normalized = normalized.replace(/[\r\n\t]/g, "");
  
    // Add required padding.
    const pad = normalized.length % 4;
    if (pad === 2) normalized += "==";
    else if (pad === 3) normalized += "=";
    else if (pad === 1) {
      throw new Error("base64ToBytes: invalid base64 length");
    }
  
    // Basic validation before calling atob (gives clearer errors).
    if (!/^[A-Za-z0-9+/]*={0,2}$/.test(normalized)) {
      throw new Error("base64ToBytes: invalid base64 characters");
    }
  
    const binary = atob(normalized);
    return Uint8Array.from(binary, (c) => c.charCodeAt(0));
  }
 
  export function base64ToUtf8(base64) {
    const bytes = base64ToBytes(base64);
    return new TextDecoder("utf-8").decode(bytes);
  }
  
  export function decodePreUse(buffer) {
    if (!(buffer instanceof Uint8Array)) {
      throw new Error("decodePreUse: expected Uint8Array");
    }
  
    // Respect byteOffset/byteLength (Uint8Array may be a view into a larger buffer).
    const view = new DataView(buffer.buffer, buffer.byteOffset, buffer.byteLength);
    let offset = 0;
  
    // Header: foreignkey (8) + count (2)
    if (view.byteLength < 10) {
      throw new Error("decodePreUse: buffer too small");
    }
  
    const foreignkey = Number(view.getBigUint64(offset));
    offset += 8;
  
    const count = view.getUint16(offset);
    offset += 2;
  
    // Each record is 20 bytes: HHBBIHQ
    const recordSize = 20;
    const expectedMin = 10 + count * recordSize;
    if (view.byteLength < expectedMin) {
      throw new Error(
        `decodePreUse: truncated buffer (need ${expectedMin} bytes, got ${view.byteLength})`
      );
    }
  
    const result = [];
  
    for (let i = 0; i < count; i++) {
      const testid = view.getUint16(offset); offset += 2;
      const name_id = view.getUint16(offset); offset += 2;
      const passed = view.getUint8(offset) !== 0; offset += 1;
      const critical = view.getUint8(offset) !== 0; offset += 1;
      const data = view.getUint32(offset); offset += 4;
      const data_id = view.getUint16(offset); offset += 2;
      let time = Number(view.getBigUint64(offset)); offset += 8;
  
      // If upstream sends unix seconds, normalize to ms for JS Date().
      if (time > 0 && time < 1e12) time *= 1000;
  
      result.push({
        foreignkey,
        testid,
        name_id,
        passed,
        critical,
        data: [{ data, id: data_id }],
        time
      });
    }
  
    return result;
  }
 
  function parseValue(raw) {
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "number") return raw;
    if (typeof raw !== "string") return raw;
 
    const trimmed = raw.trim();
    if (!trimmed) return trimmed;
 
    // Value can be stringified JSON array like "[0]" or "[1,2,3]".
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        return JSON.parse(trimmed);
      } catch (_) {
        return trimmed;
      }
    }
 
    return trimmed;
  }
 
  function parsePassFail(value) {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value !== 0;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (["1", "true", "pass", "passed", "ok", "success"].includes(normalized)) return true;
      if (["0", "false", "fail", "failed", "error"].includes(normalized)) return false;
    }
    return Boolean(value);
  }
 
  export function decodeJsonPayload(jsonObject) {
    if (!jsonObject || typeof jsonObject !== "object") {
      throw new Error("decodeJsonPayload: invalid JSON object");
    }
 
    // Supported shapes:
    // 1) { content: [...rows], machineUDI }
    // 2) { content: { preuse_tests: [...rows], versions }, machineUDI }
    // 3) [...rows]
    let rows = null;
    if (Array.isArray(jsonObject)) {
      rows = jsonObject;
    } else if (Array.isArray(jsonObject.content)) {
      rows = jsonObject.content;
    } else if (jsonObject.content && Array.isArray(jsonObject.content.preuse_tests)) {
      rows = jsonObject.content.preuse_tests;
    }

    if (!Array.isArray(rows)) {
      throw new Error("decodeJsonPayload: missing content array");
    }
 
    return rows.map((row, index) => {
      if (!Array.isArray(row) || row.length < 5) {
        throw new Error(`decodeJsonPayload: invalid row at index ${index}`);
      }
 
      // Row format: [testid, epochMs time, pass/fail, errorcode, str data array]
      const [testid, time, passFail, errorCode, rawValue] = row;
      const parsedValue = parseValue(rawValue);
      const firstValue = Array.isArray(parsedValue)
        ? Number(parsedValue[0] ?? 0)
        : Number(parsedValue);
 
      return {
        machineUDI: jsonObject.machineUDI || "",
        testid: Number(testid),
        // Keep shape compatible with existing table.
        name_id: Number(testid),
        passed: parsePassFail(passFail),
        errorcode: Number(errorCode) || 0,
        // Backward compatibility with old UI field.
        critical: (Number(errorCode) || 0) !== 0,
        data: [{ data: Number.isFinite(firstValue) ? firstValue : 0, id: 0 }],
        raw_data: parsedValue,
        time: Number(time),
      };
    });
  }
 
  export function decodePayload(encoded) {
    const bytes = base64ToBytes(encoded);
    let text = "";
 
    // Java producer: Base64 URL-safe encoded GZIP bytes.
    // Try gzip first when magic header is present: 1F 8B.
    if (bytes.length >= 2 && bytes[0] === 0x1f && bytes[1] === 0x8b) 
    {
      try {
        text = pako.ungzip(bytes, { to: "string" }).trim();
      } catch (e) {
        throw new Error(`decodePayload: failed to ungzip payload (${e?.message || "unknown error"})`);
      }
    } else {
      // Uncompressed base64(JSON) fallback.
      text = base64ToUtf8(encoded).trim();
    }
 
    // JSON payload path:
    // { content: [...], machineUDI } or
    // { content: { preuse_tests: [...], versions }, machineUDI }
    if (text.startsWith("{") || text.startsWith("[")) {
      const parsed = JSON.parse(text);
      return decodeJsonPayload(parsed);
    }
 
    // Backward compatibility: old packed binary payload.
    return decodePreUse(bytes);
  }